import { useEffect, useState, type ReactNode } from 'react';
import { preloadGoogleIdentityServices } from '../../../lib/google-calendar-code';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useLoadBi } from '../../../lib/use-load-bi';
import { AppleGlyph, GoogleGlyph, OutlookGlyph, PROVIDERS, useCalendarConnect, type Provider } from '../../CalendarConnect';
import { Modal } from '../../Modal';
import { Button, Typography } from '../ui';
import styles from './MeetingsCalendarModal.module.css';

const GOOGLE = PROVIDERS.find((p): p is Provider & { kind: 'oauth' } => p.id === 'google' && p.kind === 'oauth');

/**
 * Only Google is live. Outlook and iCloud are listed so the user sees they are coming, and drawn
 * disabled: a Connect that led nowhere would be worse than a row that plainly says "Coming soon".
 */
const ROWS: { id: 'google' | 'microsoft' | 'apple'; labelKey: string; glyph: ReactNode; available: boolean }[] = [
  { id: 'google', labelKey: 'calendar.provider.google', glyph: <GoogleGlyph />, available: true },
  { id: 'microsoft', labelKey: 'calendar.provider.microsoft', glyph: <OutlookGlyph />, available: false },
  { id: 'apple', labelKey: 'calendar.provider.apple', glyph: <AppleGlyph />, available: false },
];

/**
 * The calendar step of a Meetings connect, and what Reconnect opens.
 *
 * A row's Connect runs that provider's consent. When Google reports success the caller finishes the flow
 * on its own (`onCalendarConnected`) — the user pressed Connect once, and meant it. The footer Connect is
 * for a site that already has a calendar: it creates the meetings connection without another consent.
 *
 * Google's consent window needs a user gesture, and the press on the row's button is that gesture — which
 * is why the script is loaded when the modal opens rather than on the click.
 */
export function MeetingsCalendarModal({
  hasCalendar,
  connected,
  connecting,
  onClose,
  onCalendarConnected,
  onConnect,
}: {
  /** A calendar is already there, so the footer Connect has something to connect to. */
  hasCalendar: boolean;
  /** Opened by Reconnect: the meetings connection exists, so the footer has nothing left to create. */
  connected: boolean;
  /** The meetings connection is being created. */
  connecting: boolean;
  onClose: () => void;
  onCalendarConnected: () => void;
  onConnect: () => void;
}) {
  const { t } = useTranslation();
  const { logModalActionBi } = useGenericBi();
  const modalAction = (action: string) =>
    logModalActionBi({ page_name: 'settings-connectors', modal_name: 'meetings_calendar', action });
  const { pending, error, connectOAuth } = useCalendarConnect();
  // `.size` rather than a per-provider read, and that IS the right question here: Google is the only
  // connectable row in this modal (the other two are "coming soon"), so "anything in flight" and "my
  // provider in flight" are the same thing — and `busy` also gates the modal's own Close.
  const busy = pending.size > 0 || connecting;

  // Reported because a blocked Google script is otherwise invisible until the click fails: this is the
  // one measurement of how often an ad blocker or a network policy stops the connect before it starts.
  const [script, setScript] = useState<'loading' | 'ready' | 'failed'>('loading');
  useLoadBi('meetings-calendar-google-script', { isLoading: script === 'loading', isError: script === 'failed' });
  useEffect(() => {
    let live = true;
    void preloadGoogleIdentityServices().then((loaded) => {
      if (live) setScript(loaded ? 'ready' : 'failed');
    });
    return () => {
      live = false;
    };
  }, []);

  const connectGoogle = async () => {
    if (!GOOGLE || busy) return;
    modalAction('connect_google_calendar');
    const { connected: calendarConnected } = await connectOAuth(GOOGLE);
    if (calendarConnected) onCalendarConnected();
  };

  const cancel = () => {
    modalAction('cancel');
    onClose();
  };

  return (
    <Modal
      open
      onClose={busy ? () => {} : cancel}
      title={t('connectors.meetings.modal.title')}
      size="minimum"
      maxHeightPx={650}
      scrollBody
      footerSeparator
      footer={
        <>
          <Button variant="outlined" onClick={cancel} disabled={busy}>
            {t('common.close')}
          </Button>
          <Button
            loading={connecting}
            disabled={busy || connected || !hasCalendar}
            onClick={() => {
              modalAction('connect');
              onConnect();
            }}
          >
            {t('connectors.meetings.connect')}
          </Button>
        </>
      }
    >
      <ul className={styles.rows}>
        {ROWS.map((row) => (
          <li key={row.id} className={[styles.row, row.available ? '' : styles.rowDisabled].filter(Boolean).join(' ')}>
            <span className={styles.glyph} aria-hidden>
              {row.glyph}
            </span>
            <span className={styles.meta}>
              <Typography variant="h4" component="span" className={styles.name}>
                {t(row.labelKey)}
              </Typography>
              <Typography variant="p2" color="secondary" component="span" className={styles.subtitle}>
                {t(row.available ? 'connectors.meetings.modal.subtitle' : 'connectors.meetings.modal.soon')}
              </Typography>
            </span>
            <Button
              variant="outlined"
              size="small"
              disabled={!row.available || busy}
              loading={row.id === 'google' && pending.has('google')}
              onClick={row.id === 'google' ? () => void connectGoogle() : undefined}
            >
              {t('common.connect')}
            </Button>
          </li>
        ))}
      </ul>
      {error && (
        <Typography variant="p2" color="secondary" component="p" role="alert" className={styles.error}>
          {error}
        </Typography>
      )}
    </Modal>
  );
}
